/**
 * React hook for PostHog analytics.
 *
 * Provides convenient analytics functions that automatically
 * include team and user context from AuthContext.
 */
import { useCallback } from "react";

import { useAuth } from "./useAuth";
import {
  trackEvent as phTrackEvent,
  identifyUser as phIdentifyUser,
  identifyTeam as phIdentifyTeam,
} from "../utils/posthog";

export const useAnalytics = () => {
  const { userId, teamId, user, team } = useAuth();

  /**
   * Track an event with automatic user/team context.
   */
  const trackEvent = useCallback(
    (eventName, properties = {}) => {
      phTrackEvent(eventName, {
        ...properties,
        // Include team context if available
        ...(teamId && { team_id: teamId }),
      });
    },
    [teamId],
  );

  // Page views are auto-tracked by posthog-js capture_pageview: 'history'

  /**
   * Identify the current user with properties.
   */
  const identifyUser = useCallback(
    (properties = {}) => {
      if (!userId) return;

      const userProps = {
        ...properties,
        ...(user?.email && { email: user.email }),
        ...(user?.first_name && { first_name: user.first_name }),
      };

      phIdentifyUser(userId, userProps, teamId);
    },
    [userId, teamId, user],
  );

  /**
   * Identify the current team with properties.
   */
  const identifyTeam = useCallback(
    (properties = {}) => {
      if (!teamId) return;

      const teamProps = {
        ...properties,
        ...(team?.name && { name: team.name }),
      };

      phIdentifyTeam(teamId, teamProps);
    },
    [teamId, team],
  );

  return {
    trackEvent,
    identifyUser,
    identifyTeam,
  };
};
